import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Download, ExternalLink, FileBadge, Loader2, Pencil, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { ConfirmDialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { ActionButton } from '@components/Table';
import { CacStatusBadge, ExpiryLabel } from '@components/ExpiryBadge';
import { usePermissions } from '@hooks/usePermissions';
import cacDocumentService from '@services/cacDocumentService';
import firearmService from '@services/firearmService';
import { downloadDocument, openDocumentInNewTab } from '@pages/documents/documentActions';
import {
  CAC_DOC_STATUS_LABELS,
  CAC_DOC_TYPES,
  CAC_DOC_TYPE_LABELS,
  firearmLabel,
  type CacDocStatus,
  type CacDocStatusFilter,
  type CacDocType,
  type CacDocument,
  type Firearm,
} from '@/types/cac';
import { formatDateBR } from '@/utils/formatters';
import { CacDocumentFormModal, type CacFormMode } from './CacDocumentFormModal';

const STATUS_FILTERS: CacDocStatus[] = ['valid', 'expiring', 'expired', 'renewed'];

/**
 * Acervo CAC → Documentos (CR, CRAF, GT, porte): filtros por tipo/arma/status, status derivado e
 * dias para vencer, criar/editar/renovar/excluir. `?open=<id>` abre a edição (sino / painel Hoje);
 * `?firearm_id=` vem da ficha da arma.
 */
export const CacDocumentListView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('acervo.write').hasPermission;
  const [searchParams, setSearchParams] = useSearchParams();
  const [docType, setDocType] = useState<CacDocType | ''>('');
  const [firearmId, setFirearmId] = useState<number | ''>(() => (Number(searchParams.get('firearm_id')) > 0 ? Number(searchParams.get('firearm_id')) : ''));
  const [status, setStatus] = useState<CacDocStatusFilter>('');
  const [items, setItems] = useState<CacDocument[]>([]);
  const [firearms, setFirearms] = useState<Firearm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState<{ mode: CacFormMode; source?: CacDocument } | null>(null);
  const [deleting, setDeleting] = useState<CacDocument | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState<number | null>(null);

  useEffect(() => {
    firearmService.getAll('').then(setFirearms).catch(setError);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await cacDocumentService.getAll({ doc_type: docType, firearm_id: firearmId || undefined, status }));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [docType, firearmId, status]);

  useEffect(() => {
    load();
  }, [load]);

  // ?open=<id> → abre a edição direto e limpa o parâmetro.
  useEffect(() => {
    const openId = Number(searchParams.get('open'));
    if (openId > 0) {
      cacDocumentService
        .getById(openId)
        .then((d) => setForm({ mode: 'edit', source: d }))
        .catch(setError);
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await cacDocumentService.remove(deleting.id);
      setDeleting(null);
      load();
    } catch (err) {
      setDeleting(null);
      setError(err);
    } finally {
      setDeleteBusy(false);
    }
  };

  const run = async (id: number, fn: (id: number) => Promise<void>) => {
    setActionBusy(id);
    try {
      await fn(id);
    } catch (err) {
      setError(err);
    } finally {
      setActionBusy(null);
    }
  };

  const hasFilters = !!docType || !!firearmId || !!status;

  const fileActions = (d: CacDocument) =>
    d.document_id ? (
      <>
        <ActionButton icon={<ExternalLink size={16} />} variant="view" tooltip="Visualizar arquivo" disabled={actionBusy === d.document_id} onClick={() => run(d.document_id as number, openDocumentInNewTab)} />
        <ActionButton icon={<Download size={16} />} variant="default" tooltip="Baixar arquivo" disabled={actionBusy === d.document_id} onClick={() => run(d.document_id as number, downloadDocument)} />
      </>
    ) : null;

  const actions = (d: CacDocument) => (
    <div className="flex items-center gap-0.5 justify-end">
      {fileActions(d)}
      {canWrite && d.status !== 'renewed' && (
        <>
          <ActionButton icon={<RefreshCw size={16} />} variant="view" tooltip="Renovar" onClick={() => setForm({ mode: 'renew', source: d })} />
          <ActionButton icon={<Pencil size={16} />} variant="edit" tooltip="Editar" onClick={() => setForm({ mode: 'edit', source: d })} />
        </>
      )}
      {canWrite && <ActionButton icon={<Trash2 size={16} />} variant="delete" tooltip="Excluir" onClick={() => setDeleting(d)} />}
    </div>
  );

  /** Rótulo da arma: o do backend ou, na falta, montado a partir da lista de armas. */
  const firearmText = (d: CacDocument): string | undefined => {
    if (d.firearm_label) return d.firearm_label;
    const f = d.firearm_id ? firearms.find((x) => x.id === d.firearm_id) : undefined;
    return f ? firearmLabel(f) : undefined;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <FileBadge size={20} className="text-slate-500" /> Documentos CAC
        </h1>
        <PermissionGuard permission="acervo.write">
          <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setForm({ mode: 'create' })}>
            Novo documento
          </Button>
        </PermissionGuard>
      </div>

      <div className="card p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-label block mb-1">Tipo</label>
          <select className="select-field" value={docType} onChange={(e) => setDocType(e.target.value as CacDocType | '')}>
            <option value="">Todos</option>
            {CAC_DOC_TYPES.map((t) => (
              <option key={t} value={t}>
                {CAC_DOC_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-label block mb-1">Arma</label>
          <select className="select-field" value={firearmId} onChange={(e) => setFirearmId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Todas</option>
            {firearms.map((f) => (
              <option key={f.id} value={f.id}>
                {firearmLabel(f)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-label block mb-1">Situação</label>
          <select className="select-field" value={status} onChange={(e) => setStatus(e.target.value as CacDocStatusFilter)}>
            <option value="">Vigentes (sem renovados)</option>
            <option value="all">Todos</option>
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>
                {CAC_DOC_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
        {hasFilters && (
          <button
            type="button"
            className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 sm:col-span-3"
            onClick={() => {
              setDocType('');
              setFirearmId('');
              setStatus('');
              setSearchParams({}, { replace: true });
            }}
          >
            <X size={14} /> Limpar filtros
          </button>
        )}
      </div>

      {/* Celular: cartões */}
      <div className="md:hidden space-y-2">
        {loading && items.length === 0 && (
          <p className="text-center text-slate-400 py-6">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        )}
        {!loading && items.length === 0 && <p className="text-center text-slate-500 py-8">Nenhum documento CAC encontrado.</p>}
        {items.map((d) => (
          <div key={d.id} className={clsx('card p-3 space-y-2', loading && 'opacity-60', d.status === 'renewed' && 'opacity-70')}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-slate-800">
                  {CAC_DOC_TYPE_LABELS[d.doc_type]}
                  {d.number && <span className="text-slate-500 font-normal"> · nº {d.number}</span>}
                </p>
                {firearmText(d) && <p className="text-small">{firearmText(d)}</p>}
                {d.issuer_unit && <p className="text-small">{d.issuer_unit}</p>}
              </div>
              <CacStatusBadge status={d.status} />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-small">
              <ExpiryLabel expiresAt={d.expires_at} days={d.status === 'renewed' ? null : d.days_to_expire} />
              {actions(d)}
            </div>
          </div>
        ))}
      </div>

      {/* Tabela */}
      <div className="table-container bg-white hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th>Documento</th>
                <th>Arma</th>
                <th>Emissão</th>
                <th>Validade</th>
                <th>Situação</th>
                <th className="w-48"></th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center text-slate-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center py-12 text-slate-500">
                    Nenhum documento CAC encontrado.
                  </td>
                </tr>
              )}
              {items.map((d) => (
                <tr key={d.id} className={clsx('table-row', loading && 'opacity-60', d.status === 'renewed' && 'opacity-70')}>
                  <td className="table-cell">
                    <p className="font-medium text-slate-800">
                      {CAC_DOC_TYPE_LABELS[d.doc_type]}
                      {d.number && <span className="text-slate-500 font-normal"> · nº {d.number}</span>}
                    </p>
                    {d.issuer_unit && <p className="text-small">{d.issuer_unit}</p>}
                  </td>
                  <td className="table-cell">
                    {d.firearm_id ? (
                      <Link to={`/cac/firearms?open=${d.firearm_id}`} className="text-emerald-700 hover:underline" onClick={(e) => e.stopPropagation()}>
                        {firearmText(d) ?? `Arma #${d.firearm_id}`}
                      </Link>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="table-cell whitespace-nowrap">{d.issued_at ? formatDateBR(d.issued_at) : <span className="text-slate-400">—</span>}</td>
                  <td className="table-cell">
                    <ExpiryLabel expiresAt={d.expires_at} days={d.status === 'renewed' ? null : d.days_to_expire} />
                  </td>
                  <td className="table-cell">
                    <CacStatusBadge status={d.status} />
                  </td>
                  <td className="table-cell">{actions(d)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {form && (
        <CacDocumentFormModal
          mode={form.mode}
          source={form.source}
          firearms={firearms}
          onClose={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            load();
          }}
          onError={setError}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title="Excluir documento CAC"
        message={`Excluir ${deleting ? CAC_DOC_TYPE_LABELS[deleting.doc_type] : ''}${deleting?.number ? ` nº ${deleting.number}` : ''}? Ele deixa de aparecer e de gerar alertas.`}
        confirmText="Excluir"
        severity="danger"
        loading={deleteBusy}
      />

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
